/* eslint-disable max-lines */
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {Content, TDocumentDefinitions} from "pdfmake/interfaces";
import {PlayableAction} from "../../redux/player/types";
import {
  ActionResult,
  Challenge,
  ChallengeMode,
  ChoiceUserInputBranch,
  ChoiceUserInputBranchState,
  CompetenceAnalysisState,
  DisplayParametricResultMode,
  EducationSessionResult,
  FeedbackStatusEnumType,
  ParametricResultDescription,
  ResultDescription,
  User,
} from "../../schema";
import {TFunction} from "react-i18next";
import {
  getActionEmotionsData,
  getGeneralEmotionsData,
  getPassedOrFailedHelpText,
  getProcessedResultDescriptions,
  getResultDisplayFields,
  getScoresData,
} from "./utils";
import {getParametricScore, minutesFromDeltaTimestamp} from "../../utils";
import {getTextWithStressMarks, phraseToText} from "../../components/Phrase";
import {Action, Scenario, isUserInputAction} from "../../types";

export type EducationSessionResultsPDFData = {
  user: Pick<User, "email" | "fullName"> | null;
  challenge: Challenge;
  actions: PlayableAction[];
  additionalScenarios: Scenario[];
  results: Pick<
    EducationSessionResult,
    | "passed"
    | "score"
    | "time"
    | "usedHints"
    | "record"
    | "recordState"
    | "generalSentimentNeutral"
    | "generalSentimentPositive"
    | "generalSentimentNegative"
    | "generalEmotionAnger"
    | "generalEmotionDisgust"
    | "generalEmotionEnthusiasm"
    | "generalEmotionFear"
    | "generalEmotionGuilt"
    | "generalEmotionJoy"
    | "generalEmotionNeutral"
    | "generalEmotionSadness"
    | "generalEmotionShame"
    | "generalEmotionSurprise"
    | "competenceResults"
    | "competenceAnalysisState"
    | "parametricResults"
    | "isUserInputAnalysisReady"
    | "actionResults"
    | "resultFeedback"
    | "finishedScenariosIds"
    | "isContentMatches"
    | "initAvatarAction"
  >;
  resultDescriptions?: ResultDescription[];
  parametricResultDescriptions?: ParametricResultDescription[];
};

export function generateEducationSessionResultsReportPDF(data: EducationSessionResultsPDFData, t: TFunction) {
  // Ensure vfs is initialized before generating PDF
  const pdfMakeInstance: any = pdfMake;
  const pdfFontsInstance: any = pdfFonts;

  if (!pdfMakeInstance.vfs) {
    if (pdfFontsInstance.pdfMake && pdfFontsInstance.pdfMake.vfs) {
      pdfMakeInstance.vfs = pdfFontsInstance.pdfMake.vfs;
    } else if (pdfFontsInstance.vfs) {
      pdfMakeInstance.vfs = pdfFontsInstance.vfs;
    }
  }

  const {challenge, actions, additionalScenarios, results, resultDescriptions, parametricResultDescriptions} = data;
  const content: Content[] = [];

  const userName = data.user?.fullName ?? "";
  const userEmail = data.user?.email ?? "";

  if (data.user)
    // H1: Challenge Title
    content.push({
      text: `${t("player.results.PdfReport.titleWithUser", {
        challengeName: challenge.name,
        userName: userName,
        userEmail: userEmail,
      })}`,
      style: "h1",
      margin: [0, 0, 0, 16],
    });
  else
    // H1: Challenge Title
    content.push({
      text: `${t("player.results.PdfReport.title", {challengeName: challenge.name})}`,
      style: "h1",
      margin: [0, 0, 0, 16],
    });

  // H2: Passed / Failed
  const passedOrFailedText = getPassedOrFailedHelpText(results.passed, challenge.mode, t);
  content.push({
    text: passedOrFailedText,
    style: "h2",
    margin: [0, 0, 0, 10],
  });

  // Dynamic metrics row: Time, Score, Hints
  let displayFields = getResultDisplayFields(challenge.mode);

  const scoresColumns: Content[] = [];

  const scores = getScoresData(results, challenge.mode, t);

  scores.forEach(({field, title, getValue}) => {
    if (!displayFields.includes(field)) return;

    scoresColumns.push({
      stack: [
        {text: title, style: "h3"},
        {text: getValue(), style: "h4"},
      ],
    });
  });

  if (scoresColumns.length > 0) {
    content.push({columns: scoresColumns, columnGap: 24, margin: [0, 8, 0, 8]});
  }

  // Result message
  if (challenge.resultMessage) {
    content.push({
      text: `${challenge.resultMessage}`,
      style: "p",
      margin: [0, 14, 0, 8],
    });
  }

  // Parameters block for CUSTOM_PARAMETERS_TEST
  if (challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST) {
    content.push({
      text: `${t("player.results.PdfReport.parameters.title")}`,
      style: "h2",
      margin: [0, 14, 0, 8],
    });
    const columns: Content[] = results.parametricResults.map((pr) => {
      let value = `${getParametricScore(pr, challenge.displayParametricResultMode)}
         ${challenge.displayParametricResultMode === DisplayParametricResultMode.PERCENTAGE ? "%" : ""}`;
      return {
        stack: [
          {text: pr.parameterName ?? "", style: "h3"},
          {
            text: value,
            style: "h4",
          },
        ],
      };
    });
    if (columns.length > 0) {
      for (let i = 0; i < columns.length; i += 3) {
        const chunk = columns.slice(i, i + 3);

        content.push({
          columns: chunk,
          columnGap: 24,
          margin: [0, 4, 0, 0],
        });
      }
    }
  }

  // Emotions/Sentiment block (RPG: dialogue sentiment scores are not shown in the report)

  const sentimentItems =
    challenge.mode === ChallengeMode.RPG_MODE
      ? []
      : [
          results.generalSentimentPositive !== undefined && results.generalSentimentPositive !== null
            ? {
                label: `${t("player.results.PdfReport.sentiment.positive")}`,
                score: Math.floor(results.generalSentimentPositive ?? 0),
              }
            : undefined,
          results.generalSentimentNeutral !== undefined && results.generalSentimentNeutral !== null
            ? {
                label: `${t("player.results.PdfReport.sentiment.neutral")}`,
                score: Math.floor(results.generalSentimentNeutral ?? 0),
              }
            : undefined,
          results.generalSentimentNegative !== undefined && results.generalSentimentNegative !== null
            ? {
                label: `${t("player.results.PdfReport.sentiment.negative")}`,
                score: Math.floor(results.generalSentimentNegative ?? 0),
              }
            : undefined,
        ].filter((item): item is {label: string; score: number} => item !== undefined);

  let emotionItems: Content[] = [];

  if (results.isUserInputAnalysisReady) {
    emotionItems = getGeneralEmotionsData(results, t).map(({label, score}) => ({
      stack: [
        {text: label, style: "h3"},
        {text: `${Math.floor(score)} %`, style: "h4"},
      ],
    })) as Content[];
  }

  if (sentimentItems.length > 0 || emotionItems.length > 0) {
    content.push({
      text: `${t("player.results.PdfReport.sentiment.title")}`,
      style: "h2",
      margin: [0, 14, 0, 4],
    });
  }

  if (sentimentItems.length > 0) {
    content.push({
      text: `${t("player.results.PdfReport.sentiment.mood")}`,
      style: "h3",
      margin: [0, 2, 0, 4],
    });
    content.push({
      columns: sentimentItems.map(({label, score}) => ({
        stack: [
          {text: label, style: "h3"},
          {text: `${score} %`, style: "h4"},
        ],
      })),
      columnGap: 24,
      margin: [0, 0, 0, 8],
    });
  }

  if (emotionItems.length > 0) {
    content.push({
      text: `${t("player.results.PdfReport.emotions.title")}`,
      style: "h3",
      margin: [0, 2, 0, 4],
    });
    content.push({columns: emotionItems, columnGap: 24, margin: [0, 0, 0, 8]});
  }

  if (challenge.mode === ChallengeMode.COMPETENCE_TEST || challenge.mode === ChallengeMode.RPG_MODE) {
    if (results.resultFeedback && results.resultFeedback.feedbackStatus === FeedbackStatusEnumType.READY) {
      content.push({
        text: `${t("components.ResultsDisplay.generativeFeedback.title")}`,
        style: "h2",
        margin: [0, 14, 0, 8],
      });
      content.push({
        text: results.resultFeedback.text,
        margin: [0, 0, 0, 8],
      });
    }
  }

  if (challenge.mode === ChallengeMode.COMPETENCE_TEST || challenge.mode === ChallengeMode.RPG_MODE) {
    if (results.competenceAnalysisState === CompetenceAnalysisState.READY && results.competenceResults?.length) {
      content.push({
        text: `${t("player.results.PdfReport.competenceResults.title")}`,
        style: "h2",
        margin: [0, 14, 0, 8],
      });

      results.competenceResults.forEach((competence) => {
        content.push({
          text: `${competence.competence}: ${(competence.score * 100).toFixed(0)}%`,
          style: "h3",
          margin: [0, 8, 0, 4],
        });

        if (competence.indicators && competence.indicators.length > 0) {
          const indicatorLines = competence.indicators.map((ind) => {
            const status = ind.isPresent
              ? t("player.results.PdfReport.indicator.present")
              : t("player.results.PdfReport.indicator.notPresent");
            const exampleText = ind.example
              ? ` (${t("components.ResultsDisplay.indicator.example")}: "${ind.example}")`
              : "";

            return `${ind.indicator}: ${status}${exampleText}`;
          });

          indicatorLines.forEach((line) => {
            content.push({
              text: `  • ${line}`,
              margin: [0, 2, 0, 2],
            });
          });
        }
      });
    }
  }

  // Result descriptions
  const processedResultDescriptions = getProcessedResultDescriptions(
    results,
    resultDescriptions ?? [],
    parametricResultDescriptions ?? [],
    challenge,
  );

  if (processedResultDescriptions)
    content.push({
      text: `${t("player.results.PdfReport.resultDescriptions.title")}`,
      style: "h2",
      margin: [0, 14, 0, 8],
    });
  if (typeof processedResultDescriptions === "string") {
    content.push({
      text: processedResultDescriptions,
    });
  } else {
    processedResultDescriptions?.forEach(({parameter, message}) => {
      if (message)
        content.push({
          text: `${parameter}: ${message}`,
        });
    });
  }

  // Detailed results
  if (results.isContentMatches && challenge.showDetailedResults) {
    content.push({
      text: `${t("player.results.PdfReport.details.title")}`,
      style: "h2",
      margin: [0, 14, 0, 8],
    });

    if (challenge.mode === ChallengeMode.RPG_MODE) {
      if (results.initAvatarAction?.text) {
        content.push({
          text: `${t("player.results.PdfReport.details.initAvatarAction")}`,
          bold: true,
          margin: [0, 4, 0, 2],
        });
        content.push({
          text: results.initAvatarAction.text,
        });
      }
      (results.actionResults ?? []).forEach((actionRes) => {
        const action = actions.find((a) => a.id === actionRes.actionId);
        if (!action || !isUserInputAction(action)) {
          return;
        }
        const lines = appendActionDetails(action, actionRes, t);
        if (lines.length) content.push(...lines);
      });
    } else {
      const resultsIndex = new Map((results.actionResults ?? []).map((ar) => [ar.actionId, ar]));

      if (actions && actions.length > 0) {
        content.push({
          text: `${t("player.results.PdfReport.details.mainScenario")}`,
          style: "h3",
          margin: [0, 6, 0, 2],
          decoration: "underline",
        });
        actions.forEach((action) => {
          const actionRes = resultsIndex.get(action.id);
          const lines = appendActionDetails(action, actionRes, t);
          if (lines.length) content.push(...lines);
        });
      }

      const finishedIds = results.finishedScenariosIds ?? [];
      finishedIds.forEach((sid) => {
        const scenario = additionalScenarios?.find((s) => s.id === sid);
        if (!scenario) return;
        content.push({text: `${scenario.name}:`, style: "h3", margin: [0, 15, 0, 0], decoration: "underline"});
        scenario.actions.forEach((action) => {
          const actionRes = resultsIndex.get(action.id);
          const lines = appendActionDetails(action, actionRes, t);
          if (lines.length) content.push(...lines);
        });
      });
    }
  }

  // Document definition
  const docDefinition: TDocumentDefinitions = {
    content: content,
    styles: {
      h1: {fontSize: 20, bold: true},
      h2: {fontSize: 16, bold: true},
      h3: {fontSize: 12, bold: true},
      h4: {fontSize: 11},
    },
    defaultStyle: {
      fontSize: 10,
      lineHeight: 1.3,
    },
    pageMargins: [40, 60, 40, 60],
    footer: (currentPage, pageCount) => {
      return {
        text: `  ${t("player.results.PdfReport.details.pages", {
          current: currentPage,
          total: pageCount,
        })}`,
        alignment: "center",
        fontSize: 8,
        margin: [0, 10, 0, 0],
      };
    },
  };

  const sanitize = (s: string) => s.replace(/\s+/g, "-").replace(/[^a-zA-Zа-яА-Я0-9_-]/g, "-");

  const userFileName = `${sanitize(userName)}${userEmail ? `_${sanitize(userEmail)}` : ""}`;

  // Generate filename
  const fileName = `${sanitize(challenge.name || "challenge")}${userFileName ? `_${userFileName}` : ""}_report.pdf`;

  // Create and download PDF
  pdfMake.createPdf(docDefinition).download(fileName);
}

// Build detailed results grouped by scenarios
const appendActionDetails = (action: Action, actionResult: ActionResult | undefined, t: TFunction) => {
  const lines: Content[] = [];
  switch (action.__typename) {
    case "AvatarAction":
      lines.push({
        text: `${t("player.results.PdfReport.details.avatarAction")}`,
        bold: true,
        margin: [0, 4, 0, 2],
      });
      if (action.text) {
        const processedText = getTextWithStressMarks(action.text);
        lines.push({
          text: ` - ${t("player.results.PdfReport.details.text")}: ${processedText}`,
        });
      }
      break;
    case "SystemAction":
      lines.push({
        text: `${t("player.results.PdfReport.details.systemAction")}`,
        bold: true,
        margin: [0, 4, 0, 2],
      });
      if (action.text) {
        const processedText = getTextWithStressMarks(action.text);
        lines.push({
          text: ` - ${t("player.results.PdfReport.details.text")}: ${processedText}`,
        });
      }
      break;
    case "UserInputAction":
      lines.push({
        text: `${t("player.results.PdfReport.details.userInputAction.title")}`,
        bold: true,
        margin: [0, 4, 0, 2],
      });
      if (action.referenceText) {
        lines.push({
          text: [
            ` - ${t("player.results.PdfReport.details.userInputAction.referenceText")}: `,
            {text: phraseToText(action.referenceText), color: "#2e7d32"},
          ],
        });
      }

      if (actionResult) {
        if (actionResult.recognizedText)
          lines.push({
            text:
              ` - ${t("player.results.PdfReport.details.userInputAction.recognizedText")}: ` +
              phraseToText(actionResult.recognizedText),
          });

        lines.push({
          text:
            ` - ${t("player.results.PdfReport.details.duration")}: ` +
            minutesFromDeltaTimestamp(actionResult.duration),
        });

        if (actionResult.attempts > 1) {
          if (!actionResult.isSkipped) {
            lines.push({
              text: ` - ${t("player.results.PdfReport.details.passedFromAttempts", {count: actionResult.attempts})}`,
              color: "#c62828",
            });
          } else {
            lines.push({
              text: ` - ${t("player.results.PdfReport.details.skippedAfter", {count: actionResult.attempts})}`,
              color: "#c62828",
            });
          }
        }

        if (actionResult.isSkipped && actionResult.attempts <= 1) {
          lines.push({text: ` - ${t("player.results.PdfReport.details.skipped")}`, color: "#c62828"});
        }

        // Probable emotions if present on result (sort desc, take top 3)
        const emotions = getActionEmotionsData(actionResult, t);
        if (emotions.length > 0) {
          const parts = emotions.map((e) => `${e.label}: ${(e.score * 100).toFixed(1)}%`);
          lines.push({
            text: ` - ${t("player.results.PdfReport.details.probableEmotions")}: ${parts.join(", ")}`,
          });
        }

        if (actionResult.response) {
          lines.push({
            text: `${t("player.results.PdfReport.details.response")}`,
            bold: true,
            margin: [0, 4, 0, 2],
          });
          lines.push({
            text: ` - ${actionResult.response}`,
          });
        }
      }
      break;
    case "ChoiceUserInputAction": {
      const headerText =
        `${t("player.results.PdfReport.details.choiceUserInputAction")}` +
        (action.isMultipleChoice ? `(${t("player.results.PdfReport.details.multipleChoice")}` : "");

      lines.push({
        text: headerText,
        bold: true,
        margin: [0, 4, 0, 2],
      });

      if (actionResult) {
        // Determine chosen branch by actionResult.branchId
        if (action.branches) {
          // Multiple-choice selection (branchIds) or single selection (branchId)
          if (actionResult.branchIds) {
            const selected = actionResult.branchIds
              .map((id) => action.branches.find((b) => b.id === id))
              .filter((b): b is ChoiceUserInputBranch => Boolean(b));
            selected.forEach((b) => {
              const isCorrect = b.state === ChoiceUserInputBranchState.CORRECT;
              const label = isCorrect
                ? t("player.results.PdfReport.details.correctAnswer")
                : t("player.results.PdfReport.details.incorrectAnswer");
              const answerText = phraseToText(b.userInput.referenceText);
              lines.push({
                text: [` - ${label}: `, {text: answerText, color: isCorrect ? "#2e7d32" : "#c62828"}],
              });
            });
          } else if (actionResult.branchId) {
            const chosen = action.branches.find((b) => b.id === actionResult.branchId);
            if (chosen?.userInput?.referenceText) {
              const isCorrect = chosen.state === ChoiceUserInputBranchState.CORRECT;
              const label = isCorrect
                ? t("player.results.PdfReport.details.correctAnswer")
                : t("player.results.PdfReport.details.incorrectAnswer");
              if (!isCorrect) {
                const correctState = action.branches.find((b) => b.state === ChoiceUserInputBranchState.CORRECT);
                lines.push({
                  text: [
                    ` - ${t("player.results.PdfReport.details.correctAnswers")}: `,
                    {
                      text: correctState ? phraseToText(correctState.userInput.referenceText) : "",
                    },
                  ],
                });
              }
              lines.push({
                text: [
                  ` - ${label}: `,
                  {
                    text: phraseToText(chosen.userInput.referenceText),
                    color: isCorrect ? "#2e7d32" : "#c62828",
                  },
                ],
              });
            }
          }
        }
        // Recognized text (if any), even when no branch selected
        if (actionResult.recognizedText) {
          lines.push({
            text: ` - ${t("player.results.PdfReport.details.userText")}: ` + phraseToText(actionResult.recognizedText),
          });
        }
        // Duration
        lines.push({
          text:
            ` - ${t("player.results.PdfReport.details.duration")}: ` +
            minutesFromDeltaTimestamp(actionResult.duration),
        });
        // Attempts info if attempts > 1
        if (actionResult.attempts > 1) {
          if (!actionResult.isSkipped) {
            lines.push({
              text: ` - ${t("player.results.PdfReport.details.passedFromAttempts", {count: actionResult.attempts})}`,
              color: "#c62828",
            });
          }
        }
        if (actionResult.isSkipped) {
          if (action.isMultipleChoice) {
            lines.push({text: ` - ${t("player.results.PdfReport.details.skipped")}`, color: "#c62828"});
          } else {
            lines.push({
              text: ` - ${t("player.results.PdfReport.details.skippedAfter", {count: actionResult.attempts})}`,
              color: "#c62828",
            });
          }
        }

        // Probable emotions if present on result (sort desc, take top 3)
        const emotions = getActionEmotionsData(actionResult, t);
        if (emotions.length > 0) {
          const parts = emotions.map((e) => `${e.label}: ${(e.score * 100).toFixed(1)}%`);
          lines.push({
            text: ` - ${t("player.results.PdfReport.details.probableEmotions")}: ${parts.join(", ")}`,
          });
        }

        if (actionResult.response) {
          lines.push({
            text: `${t("player.results.PdfReport.details.response")}`,
            bold: true,
            margin: [0, 4, 0, 2],
          });
          lines.push({
            text: ` - ${actionResult.response}`,
          });
        }
      }

      break;
    }
    default:
      break;
  }
  return lines;
};
